import { createContext, useContext, useCallback, useRef, useState } from 'react'
import includes from 'lodash/includes'

import useApi from 'lib/effects/api'
import { Lead, LeadAttributes } from 'lib/models/lead'
import { productDataFunctions } from './helpers'
import createSaveQueue from './save-queue'
import { ResponseErrors, ResponseMeta } from 'lib/types'
import {
  CIVIL_PARTNERSHIP_BUT_SEPARATED,
  DISSOLVED,
  DIVORCED,
  MARRIED_BUT_SEPARATED,
  SINGLE,
  WIDOWED,
} from 'utils/enums'

const LeadContext = createContext<LeadContextType | undefined>(undefined)

export type LeadContextType = {
  fetchLead: () => Promise<{ data: Lead }>
  isContactUpdating: boolean
  isLeadLoading: boolean
  isLeadUpdating: boolean
  lead: Lead
  leadId: string
  leadSavesSettled: () => Promise<Lead>
  onSubmit: (values: LeadAttributes) => Promise<void>
  updateContact: (
    values: {
      name: string
      value: string | object
    },
    allowMergeContact?: boolean
  ) => Promise<{ data: Lead }>
  updateContactErrors: ResponseErrors
  updateContactMeta: ResponseMeta | null
  updateLeadErrors: ResponseErrors
  updateLeadMeta: ResponseMeta | null
}

const useLeadContext = (): LeadContextType => {
  const context = useContext(LeadContext)
  if (context === undefined) {
    throw new Error('useLeadContext must be used within an LeadContextProvider')
  }
  return context
}

type LeadContextProviderProps = {
  children: React.ReactNode
  leadId: string
}

const LeadContextProviderForLead = ({
  children,
  leadId,
}: LeadContextProviderProps): React.ReactElement => {
  const [lead, setLead] = useState<Lead | null>(null)
  // The newest lead the server has sent back: every successful save's
  // response, even one not shown because newer values were waiting behind it,
  // and every fetch that's shown. For code that has waited for the lead's
  // saves and needs the lead before the next render.
  const latestLead = useRef<Lead | null>(null)
  const showLead = useCallback((shownLead: Lead) => {
    latestLead.current = shownLead
    setLead(shownLead)
  }, [])
  const contactId = lead?.attributes.contactId

  const [
    {
      isLoading: isLeadUpdating,
      errors: updateLeadErrors,
      meta: updateLeadMeta,
    },
    updateLeadRequest,
  ] = useApi()

  const updateLead = useCallback(
    (id, attributes) =>
      updateLeadRequest({
        url: `/api/leads/${id}`,
        method: 'PATCH',
        data: { data: { type: 'leads', id, attributes } },
      }),
    [updateLeadRequest]
  )

  // Only the last save's response is shown: forms reset from the lead, so
  // showing an earlier save's response would undo changes made since it was
  // sent. Made once, in useState rather than useMemo, as a second queue would
  // let saves overtake each other and React may throw away a memoised value.
  const [leadSaves] = useState(() =>
    createSaveQueue(
      async (attributes) => {
        const response = (await updateLead(leadId, attributes)) as {
          data: Lead
        }
        latestLead.current = response.data
        return response
      },
      (response) => showLead(response.data)
    )
  )

  const [{ isLoading: isLeadLoading }, fetchLeadRequest] = useApi({
    isLoading: true,
  })
  const latestFetchId = useRef(0)

  // Shows the lead as the server has it once lead saves have landed. The GET
  // waits for lead saves first, and is sent again if a save was sent while it
  // was on its way, since the server may have read the lead before that save.
  // Only the latest fetch is shown.
  const fetchLead = useCallback(async () => {
    latestFetchId.current += 1
    const fetchId = latestFetchId.current
    for (;;) {
      while (leadSaves.isSaving()) await leadSaves.whenIdle()
      const sentBefore = leadSaves.sentCount()
      const response = (await fetchLeadRequest({
        url: `/api/leads/${leadId}`,
      })) as { data: Lead }
      if (fetchId !== latestFetchId.current) return response
      if (leadSaves.sentCount() === sentBefore) {
        showLead(response.data)
        return response
      }
    }
  }, [fetchLeadRequest, leadId, leadSaves, showLead])

  const [
    {
      isLoading: isContactUpdating,
      errors: updateContactErrors,
      meta: updateContactMeta,
    },
    updateContactRequest,
  ] = useApi()

  const updateContact = useCallback(
    async ({ name, value }, allowMergeContact) => {
      const key = name.split('contact.')[1]
      let attributes: { [key: string]: unknown } = { [key]: value || null }
      if (allowMergeContact) {
        attributes = { ...attributes, allowMergeContact }
      }

      await updateContactRequest({
        url: `/api/contacts/${contactId}`,
        method: 'PATCH',
        data: { data: { type: 'contacts', id: contactId, attributes, leadId } },
      })
      return fetchLead()
    },
    [contactId, leadId, fetchLead, updateContactRequest]
  )

  const onSubmit = useCallback(
    async (values) => {
      if (
        includes(
          [
            SINGLE,
            WIDOWED,
            MARRIED_BUT_SEPARATED,
            CIVIL_PARTNERSHIP_BUT_SEPARATED,
            DIVORCED,
            DISSOLVED,
          ],
          values.quoteAttributes.relationshipStatus
        )
      ) {
        values.quoteAttributes.willType = 'single'
      }

      const { product } = values as LeadAttributes
      const leadPatchData = productDataFunctions[product](values)

      await leadSaves.save(leadPatchData)
    },
    [leadSaves]
  )

  // Resolves once no save for the lead is in flight or waiting, with the lead
  // as it is then. Anything the server works out from the saved lead (such as
  // a conversion) should wait for this, or it can overtake the save it needs.
  const leadSavesSettled = useCallback(async () => {
    await leadSaves.whenIdle()
    return latestLead.current as Lead
  }, [leadSaves])

  const value = {
    lead,
    leadId,
    leadSavesSettled,
    fetchLead,
    isLeadLoading,
    onSubmit,
    isContactUpdating,
    updateContactErrors,
    updateContactMeta,
    isLeadUpdating,
    updateLeadErrors,
    updateLeadMeta,
    updateContact,
  } as LeadContextType

  return <LeadContext.Provider value={value}>{children}</LeadContext.Provider>
}

// Keyed by lead id, so moving to another lead starts again from that lead,
// rather than showing the previous lead (and letting its forms be submitted)
// under the new lead's id until the new lead has been fetched. A save still
// in flight for the previous lead carries on, but is no longer shown.
const LeadContextProvider = ({
  children,
  leadId,
}: LeadContextProviderProps): React.ReactElement => (
  <LeadContextProviderForLead key={leadId} leadId={leadId}>
    {children}
  </LeadContextProviderForLead>
)

export { LeadContextProvider, useLeadContext }
